/**
 * "You cooked this — what did you use up?"
 *
 * One toggle per ingredient the recipe actually matched. It asks rather than
 * guessing because a recipe never says how much it uses: "chicken" might be one
 * of six thighs or the whole 500g pack. The defaults are a reasonable guess
 * (a single packet is probably finished, a kilo of rice probably isn't) shown
 * as something you can correct in one tap, not applied behind your back.
 */

import { useState } from "react";

import { isCountable, suggestUsedUp } from "../features/recipes/cooked";
import type { CookChoice } from "../features/recipes/cooked";
import type { FoodItem } from "../features/food/types";
import { CATEGORY_ICONS, Icon } from "./Icon";
import { Sheet } from "./Sheet";

import "./CookSheet.css";

interface CookSheetProps {
  title: string;
  /** The items the recipe matched, in its own ingredient order. */
  used: FoodItem[];
  onConfirm: (choices: CookChoice[]) => void;
  onClose: () => void;
}

export function CookSheet({ title, used, onConfirm, onClose }: CookSheetProps) {
  // Duplicates are possible — one item can satisfy two ingredient lines — and
  // showing the same row twice would let you answer it two different ways.
  const items = [...new Map(used.map((item) => [item.id, item])).values()];

  const [usedUp, setUsedUp] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.map((item) => [item.id, suggestUsedUp(item)]))
  );

  return (
    <Sheet title={title} icon="recipes" onClose={onClose}>
      <p className="cook__intro">
        Tick anything you finished. The rest stay, and countable ones go down by
        one.
      </p>

      <ul className="cook__items">
        {items.map((item) => {
          const finished = usedUp[item.id] ?? false;
          return (
            <li key={item.id}>
              <button
                className="cook__item"
                type="button"
                aria-pressed={finished}
                onClick={() =>
                  setUsedUp((current) => ({ ...current, [item.id]: !finished }))
                }
              >
                <Icon
                  name={CATEGORY_ICONS[item.category]}
                  className="cook__item-icon"
                  data-category={item.category}
                />
                <span className="cook__item-text">
                  <span className="cook__item-name">{item.name}</span>
                  <span className="cook__item-note">
                    {finished
                      ? "Used it all"
                      : isCountable(item.unit) && item.quantity > 1
                        ? `${item.quantity} ${item.unit} → ${item.quantity - 1}`
                        : "Leave it"}
                  </span>
                </span>
                {/* The tick is the state; the words beside it say the same
                    thing, so colour is never the only indicator. */}
                <Icon
                  name={finished ? "check" : "dot"}
                  className="cook__item-mark"
                  data-on={finished || undefined}
                />
              </button>
            </li>
          );
        })}
      </ul>

      <div className="cook__actions">
        <button className="cook__cancel" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="cook__confirm"
          type="button"
          onClick={() =>
            onConfirm(items.map((item) => ({ id: item.id, usedUp: usedUp[item.id] ?? false })))
          }
        >
          Done cooking
        </button>
      </div>
    </Sheet>
  );
}
